import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Theme } from '@mui/material';
import { useMediaQuery } from '@mui/material';

type SidebarContextValue = {
  isSidebarOpen: boolean;
  onSidebarOpen: () => void;
  onSidebarClose: () => void;
};

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

SidebarContext.displayName = 'SidebarContext';

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [isSidebarOpen, setSidebarOpen] = useState(!isMobileView);

  const onSidebarOpen = useCallback(() => setSidebarOpen(true), []);
  const onSidebarClose = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    setSidebarOpen(!isMobileView);
  }, [isMobileView]);

  const value = useMemo(
    () => ({ isSidebarOpen, onSidebarOpen, onSidebarClose }),
    [isSidebarOpen, onSidebarOpen, onSidebarClose],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = () => {
  const ctx = useContext(SidebarContext);

  if (!ctx) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }

  return ctx;
};
